import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, X } from 'lucide-react';

const QUOTES = [
  // Short & Memorable
  "Vault 'Em All.",
  'Catch. Collect. Vault.',
  'Vault Every Sprite.',
  "Don't Miss the Vault.",
  'Sprite Hunt. Vault Complete.',
  'Fill the Vault.',
  'Every Sprite Belongs in the Vault.',
  'The Vault Starts Here.',
  'Collect the Rare. Vault the Best.',
  'Built for Collectors. Powered by the Vault.',
  // Collector-Themed
  'No Sprite Left Behind — Vault Them All.',
  'Your Collection. Your Vault.',
  'Every Match. Every Sprite. Every Vault.',
  'The Ultimate Sprite Vault.',
  'From Spawn to Vault.',
  'Find It. Track It. Vault It.',
  'The Hunt Ends in the Vault.',
  'Complete the Vault.',
  // Competitive
  'Only Legends Fill the Vault.',
  'Vault Like a Pro.',
  'The Best Collections Live in the Vault.',
  'Your Journey to a Full Vault Starts Here.',
  'Rare Today. Vaulted Forever.',
  // Fun / Punny
  'Vault Happens.',
  'Get Sprited. Stay Vaulted.',
  'Vault Goals.',
  'Sprite by Sprite, Vault by Vault.',
  "Vault It Before It's Gone.",
  'Vault the Hype.',
];

// Loot-Llama palette so each popup has a different bright background
const PALETTE = [
  { bg: '#ff2bd6', fg: '#ffffff' },  // hot pink
  { bg: '#9d3dff', fg: '#ffffff' },  // purple
  { bg: '#00b8ff', fg: '#3a1064' },  // cyan
  { bg: '#ffd900', fg: '#3a1064' },  // yellow
  { bg: '#10b981', fg: '#ffffff' },  // emerald
  { bg: '#fb923c', fg: '#3a1064' },  // orange
];

// Random integer in [min, max)
const ri = (min, max) => Math.floor(Math.random() * (max - min)) + min;
const pick = (arr) => arr[ri(0, arr.length)];

const VISIBLE_MS = 7000;
const MIN_GAP_MS = 20000;
const MAX_GAP_MS = 20000;
const FIRST_DELAY_MS = 3 * 60 * 1000; // 3 minutes before the very first popup
const STORAGE_KEY = 'sprite-vault:quotes-enabled:v1';

export default function RandomQuotePopup() {
  const [enabled, setEnabled] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v === null ? true : v === 'true';
    } catch { return true; }
  });
  const [active, setActive] = useState(null); // { id, text, pos, palette }
  const seenRef = useRef([]); // recent quotes to avoid repeats
  const timersRef = useRef({ next: null, hide: null });

  // Persist the on/off pref
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(enabled)); } catch { /* ignore */ }
  }, [enabled]);

  const clearTimers = () => {
    if (timersRef.current.next) clearTimeout(timersRef.current.next);
    if (timersRef.current.hide) clearTimeout(timersRef.current.hide);
    timersRef.current = { next: null, hide: null };
  };

  const showOne = () => {
    // pick a quote we haven't seen in the last 6
    let q = pick(QUOTES);
    let attempts = 0;
    while (seenRef.current.includes(q) && attempts < 6) {
      q = pick(QUOTES);
      attempts += 1;
    }
    seenRef.current = [...seenRef.current, q].slice(-6);

    // random screen position, avoid the very top (header) + bottom safe zone
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const popupW = Math.min(360, vw - 40);
    const popupH = 72;
    const left = ri(20, Math.max(40, vw - popupW - 20));
    const top  = ri(120, Math.max(140, vh - popupH - 80));

    setActive({
      id: Date.now(),
      text: q,
      pos: { left, top },
      palette: pick(PALETTE),
    });

    timersRef.current.hide = setTimeout(() => {
      setActive(null);
      timersRef.current.next = setTimeout(showOne, ri(MIN_GAP_MS, MAX_GAP_MS));
    }, VISIBLE_MS);
  };

  // Schedule loop
  useEffect(() => {
    clearTimers();
    if (!enabled) { setActive(null); return; }
    timersRef.current.next = setTimeout(showOne, FIRST_DELAY_MS);
    return () => clearTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const dismissNow = () => {
    if (timersRef.current.hide) clearTimeout(timersRef.current.hide);
    setActive(null);
    timersRef.current.next = setTimeout(showOne, ri(MIN_GAP_MS, MAX_GAP_MS));
  };

  // Tiny floating mute toggle so users can shut them off
  return (
    <>
      <button
        data-testid="quote-toggle-btn"
        onClick={() => setEnabled((v) => !v)}
        title={enabled ? 'Mute pop-up quotes' : 'Show pop-up quotes'}
        className="fixed bottom-3 right-3 z-[9998] h-9 w-9 rounded-full bg-white/90 backdrop-blur border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] flex items-center justify-center text-[#ff2bd6] hover:bg-yellow-50 transition-all"
        aria-label={enabled ? 'Mute quotes' : 'Show quotes'}
      >
        <Sparkles className={`h-4 w-4 ${enabled ? '' : 'opacity-40'}`} />
      </button>

      {active && (
        <div
          key={active.id}
          data-testid="random-quote-popup"
          className="fixed z-[9999] pointer-events-auto select-none animate-quote-pop"
          style={{ left: active.pos.left, top: active.pos.top }}
        >
          <div
            className="relative max-w-[360px] rounded-2xl border-[3px] border-[#3a1064] shadow-[0_6px_0_#3a1064] px-4 py-3 flex items-center gap-2.5 font-black -rotate-1 hover:rotate-0 transition-transform"
            style={{ background: active.palette.bg, color: active.palette.fg }}
          >
            <Sparkles className="h-5 w-5 shrink-0" strokeWidth={2.5} />
            <span className="text-sm sm:text-base leading-tight tracking-tight">
              {active.text}
            </span>
            <button
              onClick={dismissNow}
              className="h-6 w-6 rounded-full bg-white/30 hover:bg-white/50 border-2 border-current flex items-center justify-center shrink-0"
              aria-label="Dismiss"
            >
              <X className="h-3 w-3" strokeWidth={3} />
            </button>
          </div>
        </div>
      )}

      {/* Keyframes for entrance + auto fade-out */}
      <style>{`
        @keyframes quote-pop {
          0%   { transform: scale(0.6) translateY(8px); opacity: 0; }
          18%  { transform: scale(1.06) translateY(-2px); opacity: 1; }
          30%  { transform: scale(1) translateY(0);      opacity: 1; }
          85%  { transform: scale(1) translateY(0);      opacity: 1; }
          100% { transform: scale(0.94) translateY(6px); opacity: 0; }
        }
        .animate-quote-pop { animation: quote-pop ${VISIBLE_MS}ms ease-out forwards; }
      `}</style>
    </>
  );
}
